import { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { DictType, KeyValueItem } from "@/model";
import { formatJSON } from "@/util/stringUtil";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { dictToKeyValueArray } from "@/util/typeUtil";
import { useSyncForm, useConstantObjectValueHandler } from "@/hook";
import { Textarea, TextInput } from "@/component/input";
import { warningToast } from "@/component/Toast";
import { SortableItemWrapper, SortableWrapper, toSortableItem, SortableItem } from "@/component/SortableWrapper";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";


interface ConstantInputGroupProps {
    constants: DictType<string>;
    legend: string;
    onChange: (constants: DictType<string | object>) => void;
}

const ConstantInputGroup: React.FC<ConstantInputGroupProps> = (props) => {
    const { constants, legend, onChange } = props;

    const intl = useIntl();
    const isWarnToastShown = useRef<boolean>(false);
    const [formatChangedKeys, setFormatChangedKeys] = useState<string[]>([]);

    const [sortableConstants, setSortableConstants] = useState<SortableItem<KeyValueItem>[]>(
        dictToKeyValueArray(constants ?? {})
            .map(constant => toSortableItem({ key: constant.key, value: formatJSON(constant.value) }))
    );

    const { getFormatChangedKeys, transform, clear } = useConstantObjectValueHandler(sortableConstants);

    const syncFromProps = useCallback(() => {
        const newSortableConstants = dictToKeyValueArray(constants ?? {})
            .map(constant => toSortableItem({ key: constant.key, value: formatJSON(constant.value) }));
        setSortableConstants(newSortableConstants);
        clear();
        isWarnToastShown.current = false;
    }, [constants, clear]);

    useSyncForm(syncFromProps);

    const handleFormatChange = (constants: SortableItem<KeyValueItem>[]) => {
        const changedKeys = getFormatChangedKeys(constants);
        setFormatChangedKeys(changedKeys);
        if (changedKeys.length > 0 && !isWarnToastShown.current) {
            isWarnToastShown.current = true;
            warningToast(intl.formatMessage({ id: "i10007" }));
        }
    };

    const update = (constants: SortableItem<KeyValueItem>[]) => {
        handleFormatChange(constants);

        setSortableConstants(constants);
        onChange(transform(constants) as DictType<string | object>);
    };

    const onChangeKey = (index: number, key: string) => {
        const prevConstant = sortableConstants[index];
        const newConstant = { ...prevConstant, data: { ...prevConstant.data, key } };
        update(withArray(sortableConstants, index, newConstant));
    };

    const onChangeValue = (index: number, value: string) => {
        const prevConstant = sortableConstants[index];
        const newConstant = { ...prevConstant, data: { ...prevConstant.data, value } };
        update(withArray(sortableConstants, index, newConstant));
    };

    return (
        <fieldset className="flex flex-col gap-2.5">
            <legend className="sr-only">
                {legend}
            </legend>

            {formatChangedKeys.length > 0 && (
                <div role="alert"
                     className="text-invalid whitespace-pre-wrap"
                >
                    {intl.formatMessage({ id: "i10007" })}
                    {`\n${formatChangedKeys[0]}${formatChangedKeys.length > 1 ? intl.formatMessage({ id: "i10007_1" }, { count: formatChangedKeys.length }) : ""}`}
                </div>
            )}
            <SortableWrapper items={sortableConstants}
                             className="flex flex-col gap-1"
                             onDragSortEnd={update}
            >{sortableConstants.map((constant, index) => (
                    <SortableItemWrapper key={constant.id}
                                         item={constant}
                    >{dragHandle => (
                        <div key={constant.id}
                             className="w-full flex gap-4 items-start"
                        >
                            <div className="label-input-grid-label">
                                {dragHandle}
                            </div>
                            <div className="w-full flex gap-1">
                                <TextInput required
                                           aria-label={`${intl.formatMessage({ id: "b0035" })} ${index + 1}`}
                                           value={constant.data.key}
                                           placeholder={intl.formatMessage({ id: "b0035" })}
                                           onChange={e => onChangeKey(index, e.target.value)}
                                />
                                <Textarea maxRows={10}
                                          aria-label={`${intl.formatMessage({ id: "b0037" })} ${index + 1}`}
                                          value={constant.data.value}
                                          placeholder={intl.formatMessage({ id: "b0037" })}
                                          onChange={value => onChangeValue(index, value)}
                                />
                            </div>
                            <button className="mt-2.5 text-red-500 shrink-0"
                                    aria-label={intl.formatMessage({ id: "b0006" })}
                                    onClick={() => update(toSpliced(sortableConstants, index, 1))}
                            >
                                <DeleteIcon className="size-4" />
                            </button>
                        </div>
                    )}</SortableItemWrapper>
            ))}</SortableWrapper>
            <button className="p-1 self-center text-black-400"
                    aria-label={intl.formatMessage({ id: "b0009" })}
                    onClick={() => update([...sortableConstants, toSortableItem({ key: '', value: '' })])}
            >
                <AddIcon className="size-4" />
            </button>
        </fieldset>
    );
};

export default ConstantInputGroup;
